/**
 * Utility functions for working with LocalStorage
 */

/**
 * LocalStorage State Management for Chat System
 * 
 * ⚠️ CRITICAL: This is the SINGLE SOURCE OF TRUTH for all chat state
 * 
 * ARCHITECTURE RULES:
 * 1. ALL chat state MUST go through these utilities
 * 2. NEVER create React state for messages, threadIds, or chat status
 * 3. ALWAYS use these helpers for chat-related localStorage operations
 * 4. Components should ONLY read, never directly write to localStorage
 * 
 * See: docs/CHAT_SYSTEM_ARCHITECTURE.md for complete guidelines
 */

// Key for storing the last visited auth page
const LAST_AUTH_PAGE_KEY = 'lastAuthPage';
// Key for storing the task view mode preference
const TASK_VIEW_PREFERENCE_KEY = 'taskViewMode';
// Key for storing image resources filter settings
const IMAGE_FILTERS_KEY = 'imageFilters';
// Key for storing copilot sidebar state
const COPILOT_SIDEBAR_STATE_KEY = 'copilotSidebarState';

// Note: Auth session management is handled by Supabase automatically

/**
 * Save the last visited authentication page
 * @param page The auth page path ('signup' or 'signin')
 */
export const saveLastAuthPage = (page: 'signup' | 'signin'): void => {
  try {
    localStorage.setItem(LAST_AUTH_PAGE_KEY, page);
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

/**
 * Get the last visited authentication page
 * @returns The last visited auth page or 'signin' if none is stored
 */
export const getLastAuthPage = (): string => {
  try {
    const lastPage = localStorage.getItem(LAST_AUTH_PAGE_KEY);
    return lastPage === 'signup' ? '/signup' : '/signin';
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return '/signin'; // Default to signin if there's an error
  }
};

// Auth session management functions removed - Supabase handles this automatically

/**
 * Save the task view mode preference
 * @param viewMode The selected view mode ('day', 'week', or 'month')
 */
export const saveTaskViewPreference = (viewMode: 'day' | 'week' | 'month'): void => {
  try {
    localStorage.setItem(TASK_VIEW_PREFERENCE_KEY, viewMode);
  } catch (error) {
    console.error('Error saving task view preference to localStorage:', error);
  }
};

/**
 * Get the saved task view mode preference
 * @returns The saved view mode or 'week' if none is stored
 */
export const getTaskViewPreference = (): 'day' | 'week' | 'month' => {
  try {
    const preference = localStorage.getItem(TASK_VIEW_PREFERENCE_KEY) as 'day' | 'week' | 'month' | null;
    return preference || 'week'; // Default to week view if no preference exists
  } catch (error) {
    console.error('Error reading task view preference from localStorage:', error);
    return 'week'; // Default to week view if there's an error
  }
};

/**
 * Interface for storing image filter settings
 */
export interface ImageFiltersSettings {
  selectedType: string | null;
  selectedStyle: string;
  selectedInfluence: string;
  selectedBackground: string;
  width: string;
  height: string;
  activeColorIds: string[];
  activeEnvironmentIds: string[];
  activeMoodIds: string[];
  imageDescription: string;
  isAdvancedMode: boolean;
}

/**
 * Save the image filter settings to localStorage
 * @param settings The current image filter settings
 */
export const saveImageFilters = (settings: ImageFiltersSettings): void => {
  try {
    localStorage.setItem(IMAGE_FILTERS_KEY, JSON.stringify(settings));
    console.log('[localStorage] Saved image filters:', settings);
  } catch (error) {
    console.error('Error saving image filters to localStorage:', error);
  }
};

/**
 * Get the saved image filter settings from localStorage
 * @returns The saved image filter settings or null if none are stored
 */
export const getImageFilters = (): ImageFiltersSettings | null => {
  try {
    const settings = localStorage.getItem(IMAGE_FILTERS_KEY);
    if (settings) {
      const parsedSettings = JSON.parse(settings) as ImageFiltersSettings;
      console.log('[localStorage] Retrieved image filters:', parsedSettings);
      return parsedSettings;
    }
    return null;
  } catch (error) {
    console.error('Error reading image filters from localStorage:', error);
    return null;
  }
};

// Key for storing private image resources filter settings
const PRIVATE_IMAGE_FILTERS_KEY = 'privateImageFilters';

/**
 * Save the private image filter settings to localStorage
 * @param settings The current private image filter settings
 */
export const savePrivateImageFilters = (settings: ImageFiltersSettings): void => {
  try {
    localStorage.setItem(PRIVATE_IMAGE_FILTERS_KEY, JSON.stringify(settings));
    console.log('[localStorage] Saved private image filters:', settings);
  } catch (error) {
    console.error('Error saving private image filters to localStorage:', error);
  }
};

/**
 * Get the saved private image filter settings from localStorage
 * @returns The saved private image filter settings or null if none are stored
 */
export const getPrivateImageFilters = (): ImageFiltersSettings | null => {
  try {
    const settings = localStorage.getItem(PRIVATE_IMAGE_FILTERS_KEY);
    if (settings) {
      const parsedSettings = JSON.parse(settings) as ImageFiltersSettings;
      console.log('[localStorage] Retrieved private image filters:', parsedSettings);
      return parsedSettings;
    }
    return null;
  } catch (error) {
    console.error('Error reading private image filters from localStorage:', error);
    return null;
  }
};

/**
 * Interface for storing copilot sidebar state
 * 
 * ⚠️ IMPORTANT: This schema is the foundation of our localStorage-first architecture
 * Any changes here affect the entire chat system
 */
export interface CopilotSidebarState {
  activeTab: 'chat' | 'threads';
  currentThreadId?: string;
  inputValue: string;
  isModalOpen?: boolean;
  
  // ✅ CHAT SYSTEM: Message caching for localStorage-first architecture
  // This enables instant loading and perfect state synchronization
  messageCache?: Record<string, {
    messages: any[]; // MessageDisplay[] - avoiding import for utils file
    lastUpdated: number;
    hasOlderMessages: boolean;
  }>;
  
  // ✅ CHAT SYSTEM: Chat state management per thread
  // Tracks loading states, errors, and generation status
  chatState?: Record<string, {
    state: 'idle' | 'loading' | 'ready' | 'error';
    isGenerating: boolean;
    error: string | null;
    lastActivity: number;
  }>;
  
  // ✅ NEW: Thread metadata cache for synchronized thread list
  // Keeps thread list in sync with message activity
  threadMetadata?: Record<string, {
    lastMessage: string;
    lastActivity: number;
    messageCount: number;
  }>;
}

/**
 * Save the copilot sidebar state to localStorage
 * @param state The current copilot sidebar state
 */
export const saveCopilotSidebarState = (state: CopilotSidebarState): void => {
  try {
    localStorage.setItem(COPILOT_SIDEBAR_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving copilot sidebar state to localStorage:', error);
  }
};

/**
 * Get the saved copilot sidebar state from localStorage
 * @returns The saved copilot sidebar state or null if none are stored
 */
export const getCopilotSidebarState = (): CopilotSidebarState | null => {
  try {
    const state = localStorage.getItem(COPILOT_SIDEBAR_STATE_KEY);
    if (state) {
      return JSON.parse(state) as CopilotSidebarState;
    }
    return null;
  } catch (error) {
    console.error('Error reading copilot sidebar state from localStorage:', error);
    return null;
  }
};

// ✅ CHAT SYSTEM: Message cache management
// These functions provide the core caching functionality for instant message loading

/**
 * Get cached messages for a specific thread
 * 
 * ⚠️ USAGE: Only use this in ChatManager or related hooks
 * Components should get messages via ChatManager, not directly
 */
export const getMessageCache = (threadId: string): any[] => {
  const state = getCopilotSidebarState();
  return state?.messageCache?.[threadId]?.messages || [];
};

/**
 * Update message cache for a specific thread
 * 
 * ⚠️ CRITICAL: This triggers re-renders across ALL components
 * Only ChatManager should call this directly
 * 
 * @param threadId - Thread to update cache for
 * @param messages - New messages array OR function to update existing
 * @param hasOlderMessages - Whether more messages exist in database
 */
export const setMessageCache = (threadId: string, messages: any[] | ((prev: any[]) => any[]), hasOlderMessages: boolean = false): void => {
  const currentState = getCopilotSidebarState();
  const currentCache = currentState?.messageCache?.[threadId]?.messages || [];
  
  const newMessages = typeof messages === 'function' ? messages(currentCache) : messages;
  
  const newState = {
    ...currentState,
    messageCache: {
      ...currentState?.messageCache,
      [threadId]: {
        messages: newMessages,
        lastUpdated: Date.now(),
        hasOlderMessages
      }
    }
  };
  saveCopilotSidebarState(newState);
  
  // ✅ AUTO-UPDATE: Update thread metadata when messages change
  // Find the last user message for thread title
  const userMessages = newMessages.filter(msg => msg.role === 'user');
  const lastUserMessage = userMessages[userMessages.length - 1];
  if (lastUserMessage?.content) {
    updateThreadMetadata(threadId, lastUserMessage.content);
  }
  
  // ✅ REACTIVE SYSTEM: Fire custom event to trigger component re-renders
  // This ensures ALL components using ChatManager update automatically
  window.dispatchEvent(new CustomEvent('localStorage-change', {
    detail: { key: 'messageCache', threadId }
  }));
};

/**
 * Get chat state for a specific thread
 * 
 * ⚠️ USAGE: Only use this in ChatManager
 * Components should get chat state via ChatManager.chatState
 */
export const getChatState = (threadId: string) => {
  const state = getCopilotSidebarState();
  return state?.chatState?.[threadId] || {
    state: 'idle' as const,
    isGenerating: false,
    error: null,
    lastActivity: Date.now()
  };
};

/**
 * Update chat state for a specific thread
 * 
 * ⚠️ CRITICAL: This controls loading indicators and error states
 * Only ChatManager should call this directly
 * 
 * @param threadId - Thread to update state for
 * @param chatState - New chat state object
 */
export const setChatState = (threadId: string, chatState: {
  state: 'idle' | 'loading' | 'ready' | 'error';
  isGenerating: boolean;
  error: string | null;
}): void => {
  const currentState = getCopilotSidebarState();
  const newState = {
    ...currentState,
    chatState: {
      ...currentState?.chatState,
      [threadId]: {
        ...chatState,
        lastActivity: Date.now()
      }
    }
  };
  saveCopilotSidebarState(newState);
  
  // ✅ REACTIVE SYSTEM: Fire custom event for state synchronization
  window.dispatchEvent(new CustomEvent('localStorage-change', {
    detail: { key: 'chatState', threadId }
  }));
};

/**
 * Clear message cache for specific thread or all threads
 * 
 * ⚠️ USAGE: Called during cleanup operations
 * Use with caution as this removes cached data
 * 
 * @param threadId - Optional specific thread to clear, omit to clear all
 */
export const clearMessageCache = (threadId?: string): void => {
  const currentState = getCopilotSidebarState();
  if (threadId) {
    // Clear specific thread cache
    const newCache = { ...currentState?.messageCache };
    delete newCache[threadId];
    const newState = { ...currentState, messageCache: newCache };
    saveCopilotSidebarState(newState);
  } else {
    // Clear all cache - used during logout or major cleanup
    const newState = { ...currentState, messageCache: {}, chatState: {} };
    saveCopilotSidebarState(newState);
  }
  
  // ✅ REACTIVE SYSTEM: Notify all components of cache changes
  window.dispatchEvent(new CustomEvent('localStorage-change', {
    detail: { key: 'clearCache', threadId }
  }));
};

// ✅ PERFORMANCE: Cache validation and invalidation helpers

/**
 * Check if cached messages for a thread are still valid
 * 
 * ⚠️ PERFORMANCE: This prevents unnecessary database calls
 * ChatManager uses this to decide whether to reload from database
 * 
 * @param threadId - Thread to check cache validity for
 * @param maxAge - Maximum age in milliseconds (default: 5 minutes)
 * @returns true if cache is valid and fresh
 */
export const isMessageCacheValid = (threadId: string, maxAge: number = 5 * 60 * 1000): boolean => {
  const state = getCopilotSidebarState();
  const cache = state?.messageCache?.[threadId];
  if (!cache) return false;
  
  return (Date.now() - cache.lastUpdated) < maxAge;
};

/**
 * Force invalidation of message cache for a specific thread
 * 
 * ⚠️ USAGE: Call when you know data has changed externally
 * This forces ChatManager to reload from database on next access
 * 
 * @param threadId - Thread to invalidate cache for
 */
export const invalidateMessageCache = (threadId: string): void => {
  clearMessageCache(threadId);
};

// ✅ NEW: Thread metadata management for synchronized thread updates

/**
 * Update thread metadata when messages change
 * ⚠️ CRITICAL: Call this whenever messages are added/updated
 * Keeps thread list synchronized with message activity
 * 
 * ✅ PERFORMANCE OPTIMIZED: Only triggers events when order would change
 */
export const updateThreadMetadata = (threadId: string, lastMessage: string): void => {
  const currentState = getCopilotSidebarState();
  const messageCache = currentState?.messageCache?.[threadId];
  const messageCount = messageCache?.messages?.length || 0;
  const newActivity = Date.now();
  
  // ✅ OPTIMIZATION: Check if this update would actually change thread ordering
  const currentMetadata = currentState?.threadMetadata?.[threadId];
  const existingActivity = currentMetadata?.lastActivity || 0;
  
  // Get all thread activities to determine if order would change
  const allMetadata = currentState?.threadMetadata || {};
  const threadActivities = Object.entries(allMetadata)
    .filter(([id]) => id !== threadId) // Exclude current thread
    .map(([id, data]) => ({ id, activity: data.lastActivity }))
    .sort((a, b) => b.activity - a.activity);
  
  // Check if this thread would move to the top (most common case)
  const wouldMoveToTop = threadActivities.length === 0 || newActivity > threadActivities[0].activity;
  const wasAlreadyAtTop = threadActivities.length === 0 || existingActivity > threadActivities[0].activity;
  
  // Only trigger expensive re-render if order would actually change
  const shouldTriggerReorder = wouldMoveToTop !== wasAlreadyAtTop;
  
  const newState = {
    ...currentState,
    threadMetadata: {
      ...currentState?.threadMetadata,
      [threadId]: {
        lastMessage: lastMessage.substring(0, 100), // Truncate for storage
        lastActivity: newActivity,
        messageCount
      }
    }
  };
  saveCopilotSidebarState(newState);
  
  // ✅ SMART UPDATES: Only trigger thread list re-render if order would change
  if (shouldTriggerReorder) {
    console.log('[localStorage] Thread order changed, triggering re-render:', threadId);
    window.dispatchEvent(new CustomEvent('localStorage-change', {
      detail: { key: 'threadMetadata', threadId, lastMessage, orderChanged: true }
    }));
  } else {
    // Still update metadata for display purposes, but don't trigger expensive re-renders
    console.log('[localStorage] Thread metadata updated (no order change):', threadId);
    window.dispatchEvent(new CustomEvent('localStorage-change', {
      detail: { key: 'threadMetadataQuiet', threadId, lastMessage, orderChanged: false }
    }));
  }
};

/**
 * Get thread metadata from localStorage
 * ⚠️ USAGE: For thread list sorting and display
 */
export const getThreadMetadata = (threadId: string) => {
  const state = getCopilotSidebarState();
  return state?.threadMetadata?.[threadId] || {
    lastMessage: '',
    lastActivity: 0,
    messageCount: 0
  };
};

/**
 * Get all thread metadata sorted by activity
 * ⚠️ USAGE: For thread list ordering
 */
export const getAllThreadMetadata = (): Record<string, any> => {
  const state = getCopilotSidebarState();
  return state?.threadMetadata || {};
};


